import { useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
export function useEditableArray(initialEntities, toValidEntity) {
    const [itemModels, setItemModels] = useState(() => initialEntities.map(initialEntity => {
        const validEntity = toValidEntity(initialEntity);
        return {
            id: crypto.randomUUID(),
            editableEntity: initialEntity,
            initialValue: validEntity,
            resultValue: validEntity,
            isDirty: false,
            isValid: !!validEntity
        };
    }));
    const update = useCallback((entityId, editableEntity) => {
        setItemModels(currentItems => {
            const idx = currentItems.findIndex(f => f.id === entityId);
            if (idx < 0) {
                return currentItems;
            }
            const newItems = [...currentItems];
            const newItem = newItems[idx];
            const validEntity = toValidEntity(editableEntity);
            const isValid = !!validEntity;
            newItems[idx] = {
                id: entityId,
                editableEntity,
                initialValue: newItem.initialValue,
                resultValue: validEntity,
                isDirty: !isEqual(editableEntity, newItem.initialValue),
                isValid
            };
            return newItems;
        });
    }, [toValidEntity]);
    const remove = useCallback((entityId) => {
        setItemModels(currentItems => {
            const idx = currentItems.findIndex(r => r.id === entityId);
            if (idx < 0) {
                return currentItems;
            }
            const newItems = [...currentItems];
            const oldItems = newItems[idx];
            newItems[idx] = {
                id: entityId,
                initialValue: oldItems.initialValue,
                isValid: true,
                isDirty: !!oldItems.initialValue,
                editableEntity: undefined,
                resultValue: undefined
            };
            return newItems;
        });
    }, []);
    const append = useCallback((entityModel) => {
        setItemModels(currentItems => {
            const validEntity = toValidEntity(entityModel);
            const isValid = !!validEntity;
            return [
                ...currentItems,
                {
                    id: crypto.randomUUID(),
                    editableEntity: entityModel,
                    initialValue: undefined,
                    resultValue: validEntity,
                    isDirty: true,
                    isValid
                }
            ];
        });
    }, [toValidEntity]);
    const isDirty = useMemo(() => itemModels.some(f => f.isDirty), [itemModels]);
    const isValid = useMemo(() => itemModels.every(f => f.isValid), [itemModels]);
    const state = useMemo(() => ({ isDirty, isValid }), [isDirty, isValid]);
    const controls = useMemo(() => ({ update, remove, append }), [update, remove, append]);
    const resultEntities = useMemo(() => itemModels.map(f => f.resultValue).filter((e) => !!e), [itemModels]);
    const editableItemModels = useMemo(() => itemModels.filter((f) => !!f.editableEntity), [itemModels]);
    return {
        editableItemModels,
        allItemModels: itemModels,
        controls,
        state,
        resultEntities
    };
}
